import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, MapPin, Phone, Mail, Plus, Check } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import ProductVisual from "@/components/ProductVisual";
import VideoCard from "@/components/VideoCard";
import TourVideo from "@/components/TourVideo";
import { Reveal, MaskedLines, EASE } from "@/components/Reveal";
import { SectionHead, Eyebrow, CTABand } from "@/components/Section";
import { COMPANY, ASSETS, MARQUEE_ITEMS, MANIFESTO, PILLARS, PROJECTS, SDGS } from "@/data/site";

function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yProduct = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -50]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-obsidian grid-lines-dark noise-overlay">
      <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 sm:pt-40 lg:pt-44 pb-16 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <motion.div style={{ y: yText }} className="lg:col-span-7">
            <Reveal y={10}>
              <img
                src={ASSETS.logo}
                alt={`${COMPANY.name} official logo`}
                data-testid="hero-logo"
                className="h-16 w-16 sm:h-20 sm:w-20 rounded-lg ring-1 ring-white/15 mb-7"
              />
            </Reveal>
            <Reveal y={14}>
              <p className="inline-flex items-center gap-2.5 rounded-full border border-volt/25 bg-pine/60 px-4 py-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-volt animate-pulse-glow" aria-hidden="true" />
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-volt">
                  Clean-Energy Technology · Sierra Leone
                </span>
              </p>
            </Reveal>
            <MaskedLines
              lines={["Engineering a", "Cleaner, Smarter", "Energy Future."]}
              className="mt-7 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.06] text-white text-balance"
              accent={[1]}
            />
            <Reveal delay={0.55} className="max-w-xl">
              <p className="mt-7 text-base sm:text-lg leading-relaxed text-slate-400">
                {COMPANY.name} is developing innovative clean-energy technologies designed to expand access to
                reliable, sustainable and future-ready power solutions.
              </p>
            </Reveal>
            <Reveal delay={0.7}>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link
                  to="/technology"
                  data-testid="hero-cta-technology"
                  className="inline-flex items-center gap-2 rounded-md bg-volt px-7 py-3.5 font-semibold text-obsidian transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-volt-soft hover:text-obsidian active:translate-y-0"
                >
                  Explore Our Technology
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <Link
                  to="/partners"
                  data-testid="hero-cta-partner"
                  className="inline-flex items-center gap-2 rounded-md border border-white/20 px-7 py-3.5 font-semibold text-white transition-[transform,border-color,color] duration-200 hover:-translate-y-0.5 hover:border-volt/60 hover:text-volt active:translate-y-0"
                >
                  Partner With BNM
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.85}>
              <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                Waterloo · Sierra Leone &nbsp;/&nbsp; CFG-500 Prototype &nbsp;/&nbsp; 5kVA Roadmap
              </p>
            </Reveal>
          </motion.div>

          <motion.div style={{ y: yProduct }} className="lg:col-span-5">
            <Reveal delay={0.4} y={40}>
              <ProductVisual />
            </Reveal>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="manifesto-section" className="bg-mist grid-lines-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <SectionHead
          eyebrow="The BNM Manifesto"
          title="Three commitments behind everything we engineer."
        />
        <div className="mt-12 border-t border-slate-200">
          {MANIFESTO.map((ch, i) => {
            const active = open === i;
            return (
              <Reveal key={ch.index} delay={i * 0.08}>
                <div className="border-b border-slate-200">
                  <button
                    data-testid={`manifesto-chapter-${ch.index}`}
                    aria-expanded={active}
                    onClick={() => setOpen(active ? -1 : i)}
                    className="group flex w-full items-baseline gap-5 sm:gap-10 py-7 text-left"
                  >
                    <span className="font-mono text-sm text-emerald-600">{ch.index}</span>
                    <span className={`flex-1 font-display text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight transition-colors ${active ? "text-slate-900" : "text-slate-400 group-hover:text-slate-700"}`}>
                      {ch.title}
                    </span>
                    <Plus className={`h-5 w-5 shrink-0 self-center text-emerald-600 transition-transform duration-300 ${active ? "rotate-45" : ""}`} aria-hidden="true" />
                  </button>
                  <AnimatePresence initial={false}>
                    {active && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <p className="max-w-3xl pb-8 pl-9 sm:pl-[4.5rem] text-base leading-relaxed text-slate-600">
                          {ch.text}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FlagshipTeaser() {
  return (
    <section data-testid="flagship-section" className="relative overflow-hidden bg-obsidian noise-overlay">
      <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <ProductVisual caption="Flagship Technology — Carbon Free Generator" />
          </Reveal>
          <div>
            <SectionHead
              dark
              eyebrow="Flagship Technology"
              title="The BNM Carbon Free Generator"
              lead="A carbon-free generation and storage platform in active development — a 500W prototype today, engineered toward a 5kVA-class hybrid system for homes, institutions and enterprises."
            />
            <Reveal delay={0.15}>
              <ul className="mt-7 flex flex-wrap gap-3">
                {["500W Prototype", "5kVA Roadmap", "Hybrid Architecture", "Carbon-Free Design"].map((chip) => (
                  <li key={chip} className="rounded-full border border-volt/25 bg-pine/60 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-volt">
                    {chip}
                  </li>
                ))}
              </ul>
              <Link
                to="/carbon-free-generator"
                data-testid="flagship-cta"
                className="mt-8 inline-flex items-center gap-2 font-semibold text-volt hover:text-volt-soft transition-colors"
              >
                Discover the Carbon Free Generator
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyBnm() {
  return (
    <section data-testid="why-bnm-section" className="bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <SectionHead
          eyebrow="Why BNM"
          title="A technology company built for the energy realities of tomorrow."
          lead="BNM combines engineering discipline with a deep understanding of energy access in Sierra Leone and comparable markets across Africa."
        />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <article className="h-full rounded-lg border border-slate-200 bg-mist p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-emerald-300">
                <p className="font-mono text-xs text-emerald-600">0{i + 1}</p>
                <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-slate-900">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function InnovationTeaser() {
  return (
    <section data-testid="innovation-teaser" className="bg-mist">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="order-2 lg:order-1">
            <Reveal>
              <figure className="rounded-lg border border-slate-200 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1621905251918-48416bd8575a?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
                  alt="Engineer working on electrical infrastructure — representative industry imagery"
                  className="w-full h-80 object-cover"
                  loading="lazy"
                  width="1200"
                  height="800"
                />
                <figcaption className="bg-white px-5 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                  Representative industry imagery
                </figcaption>
              </figure>
            </Reveal>
          </div>
          <div className="order-1 lg:order-2">
            <SectionHead
              eyebrow="Innovation & R&D"
              title="From prototype to platform."
              lead="BNM's R&D programme moves deliberately: concept, engineering, prototype, validation — then scale. Current work centres on the Carbon Free Generator and the hybrid architectures that follow it."
            />
            <Reveal delay={0.15}>
              <Link to="/innovation" data-testid="innovation-teaser-cta" className="mt-8 inline-flex items-center gap-2 font-semibold text-emerald-700 hover:text-emerald-900 transition-colors">
                Inside BNM R&D
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProjectsTeaser() {
  return (
    <section data-testid="projects-teaser" className="bg-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <SectionHead
          eyebrow="Projects & Impact"
          title="Initiatives built around energy access."
          lead="BNM's project portfolio is growing alongside its technology. Documentation is published as programmes are formalised — never before."
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
          {PROJECTS.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1}>
              <article className="h-full rounded-lg border border-slate-200 bg-mist p-8 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-emerald-300">
                <div className="flex items-center justify-between gap-4">
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-600">{p.tag}</p>
                  <span className="rounded-full border border-dashed border-slate-300 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500">
                    {p.status}
                  </span>
                </div>
                <h3 className="mt-4 font-display text-xl sm:text-2xl font-bold tracking-tight text-slate-900">{p.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <Link to="/projects" data-testid="projects-teaser-cta" className="mt-9 inline-flex items-center gap-2 font-semibold text-emerald-700 hover:text-emerald-900 transition-colors">
            View Projects & Impact
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function SustainabilityTeaser() {
  return (
    <section data-testid="sustainability-teaser" className="bg-mist grid-lines-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <SectionHead
            eyebrow="Sustainability"
            title="Development that doesn't cost the future."
            lead="BNM's mission aligns with the UN Sustainable Development Goals for clean energy, innovation, sustainable communities and climate action."
          />
          <div className="grid grid-cols-2 gap-4">
            {SDGS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.07}>
                <div className="rounded-lg border border-slate-200 bg-white p-6 h-full">
                  <p className="font-display text-3xl font-extrabold text-emerald-600">SDG {s.n}</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{s.name}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.15}>
          <Link to="/sustainability" data-testid="sustainability-teaser-cta" className="mt-10 inline-flex items-center gap-2 font-semibold text-emerald-700 hover:text-emerald-900 transition-colors">
            Our Sustainability Commitments
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function SolarServiceBand() {
  return (
    <section data-testid="solar-service-band" className="bg-mist grid-lines-light border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <SectionHead
            eyebrow="BNM Digital Service — Live Now"
            title="BNM Solar Energy Calculator"
            lead="Know exactly what your home or business needs before investing in solar. Pick your appliances, get a free energy estimate, then unlock a professional BNM design with panels, batteries, inverter, cables and a full bill of materials."
          />
          <Reveal delay={0.1}>
            <div className="rounded-lg border border-slate-200 bg-white p-8">
              <ul className="space-y-3 text-sm text-slate-600">
                {[
                  "Free energy profile — daily use, peak load, solar size range",
                  "Full professional design with PDF report",
                  "Battery, inverter, cable and protection sizing",
                  "Built for Sierra Leone conditions by BNM engineers",
                ].map((f) => (
                  <li key={f} className="flex gap-2.5">
                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/solar"
                data-testid="home-solar-cta"
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-obsidian px-7 py-3.5 font-semibold text-white transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-pine hover:text-volt active:translate-y-0"
              >
                Calculate My Solar System
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function VisionBand() {
  return (
    <section data-testid="vision-band" className="relative overflow-hidden bg-obsidian noise-overlay border-y border-white/10">
      <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <Reveal>
          <Eyebrow dark>Future Vision</Eyebrow>
          <p className="mt-6 max-w-4xl font-display text-2xl sm:text-3xl lg:text-5xl font-bold tracking-tight leading-tight text-white text-balance">
            An Africa that generates its own clean power —{" "}
            <span className="text-volt">engineered locally, trusted globally.</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function ContactStrip() {
  return (
    <section data-testid="contact-strip" className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <SectionHead
            eyebrow="Contact"
            title="Start a conversation with BNM."
            lead="Customers, investors, institutions, researchers and media — we would like to hear from you."
          />
          <Reveal delay={0.1}>
            <ul className="space-y-4 rounded-lg border border-slate-200 bg-mist p-8">
              <li className="flex items-center gap-4">
                <MapPin className="h-5 w-5 text-emerald-600 shrink-0" aria-hidden="true" />
                <span className="text-sm text-slate-700">{COMPANY.addressLines.join(", ")}</span>
              </li>
              <li>
                <a href={COMPANY.phoneHref} data-testid="home-phone-link" className="flex items-center gap-4 text-sm text-slate-700 hover:text-emerald-700 transition-colors">
                  <Phone className="h-5 w-5 text-emerald-600 shrink-0" aria-hidden="true" />
                  {COMPANY.phoneDisplay}
                </a>
              </li>
              {COMPANY.emails.map((email) => (
                <li key={email}>
                  <a href={`mailto:${email}`} data-testid={`home-email-${email}`} className="flex items-center gap-4 text-sm text-slate-700 hover:text-emerald-700 transition-colors break-all">
                    <Mail className="h-5 w-5 text-emerald-600 shrink-0" aria-hidden="true" />
                    {email}
                  </a>
                </li>
              ))}
              <li className="pt-2">
                <Link to="/contact" data-testid="contact-strip-cta" className="inline-flex items-center gap-2 font-semibold text-emerald-700 hover:text-emerald-900 transition-colors">
                  Open the contact form
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </li>
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function TourBand() {
  return (
    <section data-testid="tour-section" className="relative overflow-hidden bg-obsidian noise-overlay border-b border-white/10">
      <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="max-w-3xl">
          <SectionHead
            eyebrow="Video Tour"
            title="Take a Tour of BNM."
            lead="Join Founder & CEO Mamadu Bah for a short guided tour — the company, the Carbon Free Generator, and how to use every tool on this website, including the Solar Energy Calculator."
            dark
          />
        </div>
        <Reveal delay={0.15} className="mt-12">
          <TourVideo />
        </Reveal>
        <p className="mt-5 text-xs text-slate-400" data-testid="tour-video-note">
          2 min 32 s · Captions included · Best with sound on
        </p>
      </div>
    </section>
  );
}

function VideoBand() {
  return (
    <section data-testid="video-band" className="bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal delay={0.1} className="order-2 lg:order-1">
            <VideoCard />
          </Reveal>
          <div className="order-1 lg:order-2">
            <SectionHead
              eyebrow="Watch It Run"
              title="Not a render. Real hardware, running."
              lead="Official footage of the BNM Carbon Free Generator in operation at the Waterloo workshop — because engineering credibility is earned with working machines, not animations."
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const orgSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: COMPANY.name,
      url: window.location.origin,
      logo: window.location.origin + "/assets/logo-official.jpg",
      email: COMPANY.emails,
      telephone: COMPANY.phoneDisplay,
      address: {
        "@type": "PostalAddress",
        streetAddress: "Prince Alfred Road, Banga Farm",
        addressLocality: "Waterloo",
        addressCountry: "SL",
      },
      sameAs: [COMPANY.socials.linkedin, COMPANY.socials.facebook, COMPANY.socials.x],
    }),
    []
  );

  return (
    <>
      <Seo
        title="BNM Sustainable Energy Limited — Clean Energy Technology, Sierra Leone"
        description="BNM Sustainable Energy Limited is a Sierra Leone clean-energy technology company developing the Carbon Free Generator and sustainable energy solutions for Africa."
        jsonLd={orgSchema}
      />
      <h1 className="sr-only">BNM Sustainable Energy Limited — Renewable and clean energy technology company in Sierra Leone</h1>
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />
      <TourBand />
      <Manifesto />
      <FlagshipTeaser />
      <VideoBand />
      <WhyBnm />
      <InnovationTeaser />
      <ProjectsTeaser />
      <SustainabilityTeaser />
      <SolarServiceBand />
      <VisionBand />
      <CTABand
        title="Let's Build the Future of Energy Together."
        text="BNM welcomes partners, investors, institutions and researchers who share our ambition for carbon-free power across Africa."
        primary={{ label: "Partner With BNM", to: "/partners", testid: "home-partner-cta" }}
        secondary={{ label: "Contact Us", to: "/contact", testid: "home-contact-cta" }}
        testid="home-partnership-cta"
      />
      <ContactStrip />
    </>
  );
}
